import type { NotificationTimezoneResponse } from "../services/notificationsApi";
import { normalizeIanaTimeZone } from "./ianaTimezones";

const STORAGE_KEY = "display_timezone_source";

export function readCachedTimezoneSource(): "auto" | "manual" | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "auto" || value === "manual" ? value : null;
  } catch {
    return null;
  }
}

export function writeCachedTimezoneSource(mode: "auto" | "manual"): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore quota / private mode
  }
}

export function clearCachedTimezoneSource(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Switch UI + reporting mode — user's saved choice in localStorage wins over GET /notifications/timezone.
 * The API often stores only an IANA string (or UTC) and omits or misreports `timezone_source`.
 */
export function getDisplayTimezoneMode(
  response: Pick<NotificationTimezoneResponse, "timezone" | "timezone_source"> | null,
  deviceZone: string,
): "auto" | "manual" {
  const cached = readCachedTimezoneSource();
  if (cached) return cached;
  if (!response) return "auto";
  return inferTimezoneModeFromApi(response, deviceZone);
}

/** First visit only — infer mode when there is no local user choice yet. */
export function inferTimezoneModeFromApi(
  response: Pick<NotificationTimezoneResponse, "timezone" | "timezone_source">,
  deviceZone: string,
): "auto" | "manual" {
  if (response.timezone_source === "auto") {
    return "auto";
  }

  if (response.timezone_source === "manual") {
    const stored = normalizeIanaTimeZone(response.timezone);
    const device = normalizeIanaTimeZone(deviceZone);
    if (stored === device) {
      return "auto";
    }
    return "manual";
  }

  return "auto";
}

/** @deprecated Use getDisplayTimezoneMode */
export function resolveTimezoneModePreference(
  response: Pick<NotificationTimezoneResponse, "timezone" | "timezone_source">,
  deviceZone: string,
): "auto" | "manual" {
  return getDisplayTimezoneMode(response, deviceZone);
}
